// src/pages/Warehouse/Warehouse.tsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import BreadCrumb from "Common/BreadCrumb";
import Tab from "Common/Components/Tab/Tab";
import { Nav } from "Common/Components/Tab/Nav";
import {
  LayoutDashboard,
  ArrowRightLeft,
  ArrowLeftRight,
  Undo2,
  PackageSearch,
  Truck,
  Database,
  Settings,
  Package,
  Weight,
  ChevronLeft,
  RefreshCw,
  Plus,
  Trash2,
  Eye,
  AlertCircle,
  Upload,
  Download,
  Save,
  Filter,
  Search,
  X,
} from "lucide-react";
import TableContainer from "Common/TableContainer";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";
import selectStyles from "Common/constants/selectStyles";
import ModalData from "Common/Components/ModalDate/ModalData";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import * as Yup from "yup";
import { useFormik } from "formik";
import { apiErrorMessage } from "Common/constants/apiErrors";

// ========== Types ==========
interface Roll {
  id: number;
  entryISO: string;
  width: string;
  thickness: string;
  color: string;
  weight: number;
  originalWeight: number;
  currentWeight: number;
  totalExited: number;
  totalReturned: number;
  supplier: string;
  batch: string;
  entryNote?: string;
  status: "in" | "out";
  receiver?: string;
  exitDoc?: string;
  exitReason?: string;
  exitNote?: string;
  returnSource?: string;
  returnDoc?: string;
  returnNote?: string;
  exitISO?: string;
  returnISO?: string;
}

interface Transaction {
  type: "entry" | "exit" | "return";
  rollId: number;
  dateISO: string;
  weight: number;
  receiver?: string;
  source?: string;
  doc?: string;
  reason?: string;
  note?: string;
}

interface DashboardStats {
  totalRolls: number;
  totalWeight: number;
  totalEntries: number;
  totalExits: number;
  colorDistribution: Record<string, number>;
  recentTransactions: (Transaction & { roll: Roll })[];
}

interface DB {
  suppliers: string[];
  rolls: Roll[];
  transactions: Transaction[];
  nextId: number;
}

// ========== Helpers ==========
const getPersianDate = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
};

const jalaliDateTime = (iso: string) => {
  const d = new Date(iso);
  return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(d);
};

const nowISO = () => new Date().toISOString();

const colorHex = (name: string) =>
({
  زرد: "#facc15",
  نارنجی: "#f97316",
  قرمز: "#ef4444",
  کرم: "#f5e6c8",
  آبی: "#3b82f6",
  سبز: "#22c55e",
  طوسی: "#94a3b8",
  مشکی: "#111827",
  سفید: "#ffffff",
}[name] || "#cbd5e1");

const formatNumber = (value: number) =>
  value.toLocaleString("fa-IR", { maximumFractionDigits: 2 });

const roundWeight = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const stockWeight = (r: Roll) => roundWeight(r.currentWeight ?? 0);
const originalWeight = (r: Roll) => roundWeight(r.originalWeight ?? r.weight ?? 0);
const outsideWeight = (r: Roll) => roundWeight((r.totalExited || 0) - (r.totalReturned || 0));

// ========== Data Helpers ==========
const DB_KEY = "sheetRollWarehouse_v1";

const defaultDB = (): DB => ({
  suppliers: ["فولاد مبارکه", "هفت الماس", "فولاد تاراز", "امیرکبیر کاشان"],
  rolls: [],
  transactions: [],
  nextId: 1001,
});

const loadDB = (): DB => {
  try {
    const saved = localStorage.getItem(DB_KEY);
    if (saved) {
      const data = JSON.parse(saved);
      return {
        suppliers: data.suppliers || defaultDB().suppliers,
        rolls: data.rolls || [],
        transactions: data.transactions || [],
        nextId: data.nextId || 1001,
      };
    }
    return defaultDB();
  } catch {
    return defaultDB();
  }
};

const saveDB = (db: DB) => {
  localStorage.setItem(DB_KEY, JSON.stringify(db));
};

// ========== Constants ==========
const WIDTHS = ["107", "108", "120", "125"];
const THICKNESSES = ["20", "25", "30", "35", "40", "45", "48", "50"];
const COLORS = ["زرد", "نارنجی", "قرمز", "کرم", "آبی", "سبز", "طوسی", "مشکی", "سفید"];
const EXIT_REASONS = ["مصرف تولید", "فروش", "مرجوعی به تأمین‌کننده", "انتقال به انبار دیگر", "ضایعات", "سایر"];

// ========== Coming Soon (placeholder) ==========
const ComingSoon: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="relative w-24 h-24 mb-4">
        <div
          className="absolute inset-0 border-4 border-dashed rounded-full animate-spin-slow"
          style={{ borderColor: "#C0151B", animationDuration: "12s", opacity: 0.6 }}
        />
        <div
          className="absolute inset-3 border-4 border-dotted rounded-full animate-spin-reverse-slow"
          style={{ borderColor: "#828285", animationDuration: "8s", opacity: 0.4 }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <Settings size={32} style={{ color: "#C0151B" }} className="animate-pulse" />
        </div>
      </div>
      <h3 className="text-xl font-bold text-slate-800 dark:text-white relative inline-block">
        در حال توسعه
        <span
          className="absolute -right-3 top-0 w-1 h-full bg-[#C0151B] animate-pulse"
          style={{ animationDuration: "0.8s" }}
        />
      </h3>
      <p className="mt-2 text-sm text-slate-500 dark:text-zink-400 max-w-sm">
        این بخش به‌زودی با امکانات جدید در اختیار شما قرار خواهد گرفت.
      </p>
      <div className="flex justify-center gap-2 mt-4">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="w-2 h-2 rounded-full bg-[#C0151B] animate-bounce"
            style={{ animationDelay: `${i * 0.15}s`, animationDuration: "1s" }}
          />
        ))}
      </div>
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 6s linear infinite;
        }
        @keyframes spin-reverse-slow {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        .animate-spin-reverse-slow {
          animation: spin-reverse-slow 4s linear infinite;
        }
      `}</style>
    </div>
  );
};

// ========== Dashboard Content ==========
const DashboardContent: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalRolls: 0,
    totalWeight: 0,
    totalEntries: 0,
    totalExits: 0,
    colorDistribution: {},
    recentTransactions: [],
  });

  const fetchData = useCallback(() => {
    setLoading(true);
    try {
      const db = loadDB();
      const rollsData = db.rolls || [];
      const transactionsData = db.transactions || [];

      const inRolls = rollsData.filter((r: Roll) => r.currentWeight > 0);
      const totalWeight = inRolls.reduce((s: number, r: Roll) => s + r.currentWeight, 0);
      const entries = transactionsData.filter((t: Transaction) => t.type === "entry").length;
      const exits = transactionsData.filter((t: Transaction) => t.type === "exit").length;

      const colorDist: Record<string, number> = {};
      inRolls.forEach((r: Roll) => {
        colorDist[r.color] = (colorDist[r.color] || 0) + 1;
      });

      const recent = [...transactionsData]
        .sort((a, b) => new Date(b.dateISO).getTime() - new Date(a.dateISO).getTime())
        .slice(0, 8)
        .map((t) => ({
          ...t,
          roll: rollsData.find((r: Roll) => r.id === t.rollId),
        }))
        .filter((t): t is typeof t & { roll: Roll } => Boolean(t.roll));

      setStats({
        totalRolls: inRolls.length,
        totalWeight,
        totalEntries: entries,
        totalExits: exits,
        colorDistribution: colorDist,
        recentTransactions: recent,
      });
    } catch (error) {
      console.error("Error loading warehouse data:", error);
      Swal.fire("خطا", apiErrorMessage(error, "مشکلی در بارگذاری اطلاعات انبار رخ داد"), "error");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    Swal.fire("اطلاعیه", "اطلاعات این بخش صرفاً جهت نمایش است و به سرور متصل نمی‌باشد؛ این بخش هم‌اکنون در حال توسعه است.", "info")
  })
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = useMemo(
    () => [
      {
        header: "شناسه",
        accessorKey: "roll.id",
        cell: (cell: any) => <span className="font-medium">{cell.getValue()}</span>,
      },
      {
        header: "نوع عملیات",
        accessorKey: "type",
        cell: (cell: any) => {
          const type = cell.getValue();
          const labels: Record<string, { label: string; className: string }> = {
            entry: { label: "ورود", className: "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400" },
            exit: { label: "خروج", className: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400" },
            return: { label: "بازگشت", className: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400" },
          };
          const info = labels[type] || { label: type, className: "bg-gray-100 text-gray-700" };
          return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${info.className}`}>{info.label}</span>;
        },
      },
      {
        header: "مشخصات رول",
        cell: (cell: any) => {
          const roll = cell.row.original.roll;
          return (
            <div className="text-sm">
              <div className="font-medium">بچ {roll.batch}</div>
              <div className="text-xs text-slate-500 dark:text-zink-400">
                عرض {roll.width} | ضخامت {roll.thickness} | {roll.color}
              </div>
            </div>
          );
        },
      },
      {
        header: "وزن",
        accessorKey: "weight",
        cell: (cell: any) => <span>{formatNumber(cell.getValue())} کیلو</span>,
      },
      {
        header: "تاریخ",
        accessorKey: "dateISO",
        cell: (cell: any) => <span className="text-xs">{jalaliDateTime(cell.getValue())}</span>,
      },
    ],
    []
  );

  return (
    <>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <div className="card border-0 shadow-md rounded-2xl">
          <div className="card-body p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-zink-400">تعداد رول موجود</p>
                <h4 className="text-2xl font-bold mt-1 text-slate-800 dark:text-white">
                  {loading ? "..." : formatNumber(stats.totalRolls)}
                </h4>
                <p className="text-xs text-slate-400 mt-1">رول فعال در انبار</p>
              </div>
              <div className="size-12 rounded-xl bg-custom-500/10 flex items-center justify-center text-custom-500">
                <Package className="size-6" />
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-md rounded-2xl">
          <div className="card-body p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-zink-400">وزن موجودی</p>
                <h4 className="text-2xl font-bold mt-1 text-slate-800 dark:text-white">
                  {loading ? "..." : formatNumber(stats.totalWeight)}
                </h4>
                <p className="text-xs text-slate-400 mt-1">کیلوگرم</p>
              </div>
              <div className="size-12 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                <Weight className="size-6" />
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-md rounded-2xl">
          <div className="card-body p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-zink-400">کل ورودها</p>
                <h4 className="text-2xl font-bold mt-1 text-slate-800 dark:text-white">
                  {loading ? "..." : formatNumber(stats.totalEntries)}
                </h4>
                <p className="text-xs text-slate-400 mt-1">از ابتدای ثبت اطلاعات</p>
              </div>
              <div className="size-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                <ArrowRightLeft className="size-6" />
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-md rounded-2xl">
          <div className="card-body p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-zink-400">کل خروج‌ها</p>
                <h4 className="text-2xl font-bold mt-1 text-slate-800 dark:text-white">
                  {loading ? "..." : formatNumber(stats.totalExits)}
                </h4>
                <p className="text-xs text-slate-400 mt-1">رول خارج‌شده</p>
              </div>
              <div className="size-12 rounded-xl bg-orange-500/10 flex items-center justify-center text-orange-500">
                <ArrowLeftRight className="size-6" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 mt-5 lg:grid-cols-3">
        <div className="lg:col-span-2 card border-0 shadow-md rounded-2xl">
          <div className="card-body p-5">
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-base font-semibold text-slate-800 dark:text-white">ثبت‌های اخیر</h5>
              <Link to="/warehouse/inventory" className="text-sm text-custom-500 hover:text-custom-600">
                مشاهده همه <ChevronLeft className="inline size-4" />
              </Link>
            </div>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
              </div>
            ) : stats.recentTransactions.length > 0 ? (
              <TableContainer
                isPagination={false}
                columns={columns}
                data={stats.recentTransactions}
                divclassName="-mx-5 overflow-x-auto"
                tableclassName="w-full whitespace-nowrap"
                theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600 text-xs"
                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80 text-sm"
              />
            ) : (
              <div className="text-center py-8 text-slate-400 dark:text-zink-400">
                <p>هنوز تراکنشی ثبت نشده است</p>
              </div>
            )}
          </div>
        </div>

        <div className="card border-0 shadow-md rounded-2xl">
          <div className="card-body p-5">
            <h5 className="text-base font-semibold text-slate-800 dark:text-white mb-4">ترکیب موجودی بر اساس رنگ</h5>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
              </div>
            ) : Object.keys(stats.colorDistribution).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.colorDistribution)
                  .sort((a, b) => b[1] - a[1])
                  .map(([color, count]) => {
                    const max = Math.max(1, ...Object.values(stats.colorDistribution));
                    const percent = (count / max) * 100;
                    return (
                      <div key={color} className="flex items-center gap-3">
                        <span className="flex items-center gap-1.5 text-sm w-16">
                          <span className="inline-block size-3 rounded-full" style={{ background: colorHex(color) }} />
                          {color}
                        </span>
                        <div className="flex-1 h-2 bg-slate-200 dark:bg-zink-500 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${percent}%`,
                              background: `linear-gradient(90deg, ${colorHex(color)}, ${colorHex(color)}cc)`,
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium text-slate-700 dark:text-zink-200 min-w-[3rem] text-left">
                          {formatNumber(count)}
                        </span>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400 dark:text-zink-400">
                <p>موجودی برای نمایش وجود ندارد</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-5">
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-custom-500 border border-custom-500 rounded-lg hover:bg-custom-500 hover:text-white transition-colors dark:border-custom-400 dark:text-custom-400 dark:hover:bg-custom-400/20"
        >
          <RefreshCw className="size-4" />
          بروزرسانی
        </button>
      </div>
    </>
  );
};

// ========== Entry Content ==========
const EntryContent: React.FC = () => {
  const [db, setDb] = useState<DB>(() => loadDB());
  const [reload, setReload] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const entryForm = useFormik({
    initialValues: {
      width: "",
      thickness: "",
      color: "",
      weight: "",
      supplier: "",
      batch: "",
      entryNote: "",
    },
    validationSchema: Yup.object({
      width: Yup.string().required("لطفاً عرض را انتخاب کنید"),
      thickness: Yup.string().required("لطفاً ضخامت را انتخاب کنید"),
      color: Yup.string().required("لطفاً رنگ را انتخاب کنید"),
      weight: Yup.number()
        .typeError("عدد وارد کنید")
        .positive("وزن باید مثبت باشد")
        .required("لطفاً وزن را وارد کنید"),
      supplier: Yup.string().required("لطفاً تأمین‌کننده را انتخاب کنید"),
      batch: Yup.string().required("لطفاً شماره بچ را وارد کنید"),
    }),
    onSubmit: async (values) => {
      const weight = roundWeight(Number(values.weight));
      if (weight <= 0) {
        Swal.fire("خطا", "وزن باید عددی بزرگ‌تر از صفر باشد", "error");
        return;
      }

      setSubmitting(true);
      try {
        const newRoll: Roll = {
          id: db.nextId++,
          entryISO: nowISO(),
          width: values.width,
          thickness: values.thickness,
          color: values.color,
          weight: weight,
          originalWeight: weight,
          currentWeight: weight,
          totalExited: 0,
          totalReturned: 0,
          supplier: values.supplier,
          batch: values.batch.trim(),
          entryNote: values.entryNote.trim(),
          status: "in",
        };

        const newTransaction: Transaction = {
          type: "entry",
          rollId: newRoll.id,
          dateISO: newRoll.entryISO,
          weight: weight,
        };

        const updatedDb = {
          ...db,
          rolls: [...db.rolls, newRoll],
          transactions: [...db.transactions, newTransaction],
          nextId: db.nextId,
        };

        saveDB(updatedDb);
        setDb(updatedDb);
        setReload(!reload);
        entryForm.resetForm();
        Swal.fire("عملیات موفق", `رول با شناسه ${newRoll.id} وارد انبار شد.`, "success");
      } catch (error) {
        console.error("Entry error:", error);
        Swal.fire("خطا", apiErrorMessage(error, "مشکل در ثبت ورود"), "error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const recentEntries = useMemo(() => {
    return [...db.transactions]
      .filter((t: Transaction) => t.type === "entry")
      .sort((a, b) => new Date(b.dateISO).getTime() - new Date(a.dateISO).getTime())
      .slice(0, 8)
      .map((t) => ({
        ...t,
        roll: db.rolls.find((r: Roll) => r.id === t.rollId),
      }))
      .filter((t) => t.roll);
  }, [db, reload]);

  const entryColumns = useMemo(
    () => [
      {
        header: "شناسه",
        accessorKey: "roll.id",
        cell: (cell: any) => <span className="font-medium">{cell.getValue()}</span>,
      },
      {
        header: "عرض",
        accessorKey: "roll.width",
      },
      {
        header: "ضخامت",
        accessorKey: "roll.thickness",
      },
      {
        header: "رنگ",
        cell: (cell: any) => {
          const color = cell.row.original.roll.color;
          return (
            <span className="flex items-center gap-1.5">
              <span className="inline-block size-3 rounded-full" style={{ background: colorHex(color) }} />
              {color}
            </span>
          );
        },
      },
      {
        header: "وزن",
        accessorKey: "weight",
        cell: (cell: any) => <span>{formatNumber(cell.getValue())} کیلو</span>,
      },
      {
        header: "تأمین‌کننده",
        accessorKey: "roll.supplier",
      },
    ],
    []
  );

  const widthOptions = WIDTHS.map((w) => ({ value: w, label: w }));
  const thicknessOptions = THICKNESSES.map((t) => ({ value: t, label: t }));
  const colorOptions = COLORS.map((c) => ({ value: c, label: c }));
  const supplierOptions = db.suppliers.map((s: string) => ({ value: s, label: s }));

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <div className="card border-0 shadow-md rounded-2xl">
        <div className="card-body p-5">
          <h5 className="text-base font-semibold text-slate-800 dark:text-white mb-4">فرم ورود کالا</h5>
          <div className="bg-blue-50 dark:bg-blue-500/10 rounded-lg p-3 mb-4 text-sm text-blue-700 dark:text-blue-300">
            پس از ثبت، رول با وضعیت «موجود در انبار» ذخیره می‌شود.
          </div>

          <form onSubmit={entryForm.handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  شناسه خودکار رول
                </label>
                <input
                  type="text"
                  className="form-input w-full bg-slate-100 dark:bg-zink-600/50 text-slate-500 dark:text-zink-300 cursor-not-allowed"
                  value={db.nextId}
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  تاریخ و ساعت شمسی
                </label>
                <input
                  type="text"
                  className="form-input w-full bg-slate-100 dark:bg-zink-600/50 text-slate-500 dark:text-zink-300 cursor-not-allowed"
                  value={jalaliDateTime(new Date().toISOString())}
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  عرض ورق <span className="text-red-500">*</span>
                </label>
                <Select
                  options={widthOptions}
                  value={widthOptions.find((o) => o.value === entryForm.values.width) || null}
                  onChange={(opt: any) => entryForm.setFieldValue("width", opt?.value || "")}
                  placeholder="انتخاب کنید"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {entryForm.touched.width && entryForm.errors.width && (
                  <p className="text-red-400 text-xs mt-1">{entryForm.errors.width}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  ضخامت ورق <span className="text-red-500">*</span>
                </label>
                <Select
                  options={thicknessOptions}
                  value={thicknessOptions.find((o) => o.value === entryForm.values.thickness) || null}
                  onChange={(opt: any) => entryForm.setFieldValue("thickness", opt?.value || "")}
                  placeholder="انتخاب کنید"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {entryForm.touched.thickness && entryForm.errors.thickness && (
                  <p className="text-red-400 text-xs mt-1">{entryForm.errors.thickness}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  رنگ <span className="text-red-500">*</span>
                </label>
                <Select
                  options={colorOptions}
                  value={colorOptions.find((o) => o.value === entryForm.values.color) || null}
                  onChange={(opt: any) => entryForm.setFieldValue("color", opt?.value || "")}
                  placeholder="انتخاب کنید"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  formatOptionLabel={(opt: any) => (
                    <span className="flex items-center gap-2">
                      <span className="inline-block size-3 rounded-full" style={{ background: colorHex(opt.value) }} />
                      {opt.label}
                    </span>
                  )}
                />
                {entryForm.touched.color && entryForm.errors.color && (
                  <p className="text-red-400 text-xs mt-1">{entryForm.errors.color}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  وزن (کیلوگرم) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="form-input w-full"
                  placeholder="مثال: 1250"
                  {...entryForm.getFieldProps("weight")}
                />
                {entryForm.touched.weight && entryForm.errors.weight && (
                  <p className="text-red-400 text-xs mt-1">{entryForm.errors.weight}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  تأمین‌کننده <span className="text-red-500">*</span>
                </label>
                <Select
                  options={supplierOptions}
                  value={supplierOptions.find((o) => o.value === entryForm.values.supplier) || null}
                  onChange={(opt: any) => entryForm.setFieldValue("supplier", opt?.value || "")}
                  placeholder="انتخاب تأمین‌کننده"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {entryForm.touched.supplier && entryForm.errors.supplier && (
                  <p className="text-red-400 text-xs mt-1">{entryForm.errors.supplier}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  شماره بچ <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="form-input w-full"
                  placeholder="مثال: B-1405-001"
                  maxLength={80}
                  {...entryForm.getFieldProps("batch")}
                />
                {entryForm.touched.batch && entryForm.errors.batch && (
                  <p className="text-red-400 text-xs mt-1">{entryForm.errors.batch}</p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  توضیحات ورود (اختیاری)
                </label>
                <textarea
                  className="form-input w-full resize-y min-h-[60px]"
                  placeholder="توضیحات تکمیلی درباره رول، بار یا محل نگهداری"
                  maxLength={500}
                  {...entryForm.getFieldProps("entryNote")}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-white bg-custom-500 hover:bg-custom-600 rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-white border-t-transparent" />
                    در حال ثبت...
                  </>
                ) : (
                  <>
                    <Plus className="size-4" />
                    ثبت ورود به انبار
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => entryForm.resetForm()}
                className="px-6 py-2.5 bg-slate-200 dark:bg-zink-600 hover:bg-slate-300 dark:hover:bg-zink-500 rounded-lg transition-colors"
              >
                پاک کردن فرم
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card border-0 shadow-md rounded-2xl">
        <div className="card-body p-5">
          <h5 className="text-base font-semibold text-slate-800 dark:text-white mb-4">آخرین رول‌های واردشده</h5>
          {recentEntries.length > 0 ? (
            <TableContainer
              isPagination={false}
              columns={entryColumns}
              data={recentEntries}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600 text-xs"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80 text-sm"
            />
          ) : (
            <div className="text-center py-8 text-slate-400 dark:text-zink-400">
              <p>رولی ثبت نشده است</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ========== Exit Content ==========
const ExitContent: React.FC = () => {
  const [db, setDb] = useState<DB>(() => loadDB());
  const [reload, setReload] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRoll, setSelectedRoll] = useState<Roll | null>(null);

  const exitForm = useFormik({
    initialValues: {
      rollId: "",
      weight: "",
      receiver: "",
      exitDoc: "",
      exitReason: "",
      exitNote: "",
    },
    validationSchema: Yup.object({
      rollId: Yup.string().required("لطفاً رول را انتخاب کنید"),
      weight: Yup.number()
        .typeError("عدد وارد کنید")
        .positive("وزن خروج باید مثبت باشد")
        .required("لطفاً وزن خروج را وارد کنید"),
      receiver: Yup.string().required("لطفاً تحویل‌گیرنده را وارد کنید"),
      exitReason: Yup.string().required("لطفاً دلیل خروج را انتخاب کنید"),
    }),
    onSubmit: async (values) => {
      const roll = db.rolls.find((r: Roll) => String(r.id) === values.rollId);
      if (!roll) {
        Swal.fire("خطا", "رول انتخاب‌شده یافت نشد", "error");
        return;
      }

      const available = stockWeight(roll);
      const amount = roundWeight(Number(values.weight));

      if (amount <= 0 || amount > available) {
        Swal.fire("خطا", `وزن خروج باید بین صفر و ${formatNumber(available)} کیلوگرم باشد.`, "error");
        return;
      }

      setSubmitting(true);
      try {
        const dateISO = nowISO();
        const updatedRoll: Roll = {
          ...roll,
          currentWeight: roundWeight(available - amount),
          totalExited: roundWeight((roll.totalExited || 0) + amount),
          status: roundWeight(available - amount) > 0 ? "in" : "out",
          exitISO: dateISO,
          receiver: values.receiver.trim(),
          exitDoc: values.exitDoc.trim(),
          exitReason: values.exitReason,
          exitNote: values.exitNote.trim(),
        };

        const transaction: Transaction = {
          type: "exit",
          rollId: roll.id,
          dateISO,
          weight: amount,
          receiver: values.receiver.trim(),
          doc: values.exitDoc.trim(),
          reason: values.exitReason,
          note: values.exitNote.trim(),
        };

        const updatedDb = {
          ...db,
          rolls: db.rolls.map((r: Roll) => (r.id === roll.id ? updatedRoll : r)),
          transactions: [...db.transactions, transaction],
        };

        saveDB(updatedDb);
        setDb(updatedDb);
        setReload(!reload);
        exitForm.resetForm();
        setSelectedRoll(null);
        Swal.fire("عملیات موفق", `خروج ${formatNumber(amount)} کیلوگرم از بچ ${roll.batch} ثبت شد.`, "success");
      } catch (error) {
        console.error("Exit error:", error);
        Swal.fire("خطا", apiErrorMessage(error, "مشکل در ثبت خروج"), "error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const availableRolls = useMemo(() => {
    return db.rolls.filter((r: Roll) => stockWeight(r) > 0).sort((a, b) => String(a.batch).localeCompare(String(b.batch), "fa"));
  }, [db, reload]);

  const rollOptions = availableRolls.map((r: Roll) => ({
    value: String(r.id),
    label: `بچ ${r.batch} | شناسه ${r.id} | موجودی ${formatNumber(stockWeight(r))} کیلو | عرض ${r.width} | ضخامت ${r.thickness} | ${r.color}`,
  }));

  const exitReasonsOptions = EXIT_REASONS.map((r) => ({ value: r, label: r }));

  const handleRollChange = (opt: any) => {
    exitForm.setFieldValue("rollId", opt?.value || "");
    const roll = db.rolls.find((r: Roll) => String(r.id) === opt?.value);
    setSelectedRoll(roll || null);
    if (roll) {
      exitForm.setFieldValue("weight", String(stockWeight(roll)));
    } else {
      exitForm.setFieldValue("weight", "");
    }
  };

  const recentExits = useMemo(() => {
    return [...db.transactions]
      .filter((t: Transaction) => t.type === "exit")
      .sort((a, b) => new Date(b.dateISO).getTime() - new Date(a.dateISO).getTime())
      .slice(0, 8)
      .map((t) => ({
        ...t,
        roll: db.rolls.find((r: Roll) => r.id === t.rollId),
      }))
      .filter((t) => t.roll);
  }, [db, reload]);

  const exitColumns = useMemo(
    () => [
      {
        header: "شماره بچ",
        accessorKey: "roll.batch",
      },
      {
        header: "شناسه",
        accessorKey: "roll.id",
        cell: (cell: any) => <span className="font-medium">{cell.getValue()}</span>,
      },
      {
        header: "مشخصات رول",
        cell: (cell: any) => {
          const roll = cell.row.original.roll;
          return (
            <div className="text-sm">
              <div>عرض {roll.width} | ضخامت {roll.thickness}</div>
              <div className="text-xs text-slate-500 dark:text-zink-400">{roll.color}</div>
            </div>
          );
        },
      },
      {
        header: "وزن خروجی",
        accessorKey: "weight",
        cell: (cell: any) => <span>{formatNumber(cell.getValue())} کیلو</span>,
      },
      {
        header: "مقصد",
        accessorKey: "receiver",
        cell: (cell: any) => cell.getValue() || "—",
      },
      {
        header: "تاریخ خروج",
        accessorKey: "dateISO",
        cell: (cell: any) => <span className="text-xs">{jalaliDateTime(cell.getValue())}</span>,
      },
    ],
    []
  );

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <div className="card border-0 shadow-md rounded-2xl">
        <div className="card-body p-5">
          <h5 className="text-base font-semibold text-slate-800 dark:text-white mb-4">فرم خروج کالا</h5>

          <form onSubmit={exitForm.handleSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  انتخاب رول موجود <span className="text-red-500">*</span>
                </label>
                <Select
                  options={rollOptions}
                  value={rollOptions.find((o) => o.value === exitForm.values.rollId) || null}
                  onChange={handleRollChange}
                  placeholder="انتخاب رول..."
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {exitForm.touched.rollId && exitForm.errors.rollId && (
                  <p className="text-red-400 text-xs mt-1">{exitForm.errors.rollId}</p>
                )}
              </div>

              {selectedRoll && (
                <div className="bg-orange-50 dark:bg-orange-500/10 rounded-lg p-3 text-sm text-orange-700 dark:text-orange-300">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="size-4 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-medium">شماره بچ {selectedRoll.batch}</span>، شناسه {selectedRoll.id}
                      <br />
                      موجودی فعلی: <strong>{formatNumber(stockWeight(selectedRoll))}</strong> کیلوگرم
                      <br />
                      مقدار خارج از انبار: {formatNumber(outsideWeight(selectedRoll))} کیلوگرم
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  وزن خروجی (کیلوگرم) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="form-input w-full"
                  placeholder="مقدار خروج"
                  {...exitForm.getFieldProps("weight")}
                />
                {exitForm.touched.weight && exitForm.errors.weight && (
                  <p className="text-red-400 text-xs mt-1">{exitForm.errors.weight}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  تحویل‌گیرنده / مقصد <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="form-input w-full"
                  placeholder="نام شخص، واحد یا مشتری"
                  maxLength={100}
                  {...exitForm.getFieldProps("receiver")}
                />
                {exitForm.touched.receiver && exitForm.errors.receiver && (
                  <p className="text-red-400 text-xs mt-1">{exitForm.errors.receiver}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  شماره حواله (اختیاری)
                </label>
                <input
                  type="text"
                  className="form-input w-full"
                  placeholder="مثال: H-1024"
                  maxLength={80}
                  {...exitForm.getFieldProps("exitDoc")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  دلیل خروج <span className="text-red-500">*</span>
                </label>
                <Select
                  options={exitReasonsOptions}
                  value={exitReasonsOptions.find((o) => o.value === exitForm.values.exitReason) || null}
                  onChange={(opt: any) => exitForm.setFieldValue("exitReason", opt?.value || "")}
                  placeholder="انتخاب کنید"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {exitForm.touched.exitReason && exitForm.errors.exitReason && (
                  <p className="text-red-400 text-xs mt-1">{exitForm.errors.exitReason}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  توضیحات خروج (اختیاری)
                </label>
                <textarea
                  className="form-input w-full resize-y min-h-[60px]"
                  placeholder="توضیحات تکمیلی"
                  maxLength={500}
                  {...exitForm.getFieldProps("exitNote")}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-white border-t-transparent" />
                    در حال ثبت...
                  </>
                ) : (
                  <>
                    <ArrowLeftRight className="size-4" />
                    ثبت خروج از انبار
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  exitForm.resetForm();
                  setSelectedRoll(null);
                }}
                className="px-6 py-2.5 bg-slate-200 dark:bg-zink-600 hover:bg-slate-300 dark:hover:bg-zink-500 rounded-lg transition-colors"
              >
                پاک کردن فرم
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card border-0 shadow-md rounded-2xl">
        <div className="card-body p-5">
          <h5 className="text-base font-semibold text-slate-800 dark:text-white mb-4">آخرین خروج‌ها</h5>
          {recentExits.length > 0 ? (
            <TableContainer
              isPagination={false}
              columns={exitColumns}
              data={recentExits}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600 text-xs"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80 text-sm"
            />
          ) : (
            <div className="text-center py-8 text-slate-400 dark:text-zink-400">
              <p>خروجی ثبت نشده است</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ========== Return Content ==========
const ReturnContent: React.FC = () => {
  const [db, setDb] = useState<DB>(() => loadDB());
  const [reload, setReload] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRoll, setSelectedRoll] = useState<Roll | null>(null);

  const returnForm = useFormik({
    initialValues: {
      rollId: "",
      weight: "",
      returnSource: "",
      returnDoc: "",
      returnNote: "",
    },
    validationSchema: Yup.object({
      rollId: Yup.string().required("لطفاً رول را انتخاب کنید"),
      weight: Yup.number()
        .typeError("عدد وارد کنید")
        .positive("وزن بازگشتی باید مثبت باشد")
        .required("لطفاً وزن بازگشتی را وارد کنید"),
      returnSource: Yup.string().required("لطفاً بازگرداننده را وارد کنید"),
    }),
    onSubmit: async (values) => {
      const roll = db.rolls.find((r: Roll) => String(r.id) === values.rollId);
      if (!roll) {
        Swal.fire("خطا", "رول انتخاب‌شده یافت نشد", "error");
        return;
      }

      const available = outsideWeight(roll);
      const amount = roundWeight(Number(values.weight));

      if (amount <= 0 || amount > available) {
        Swal.fire("خطا", `وزن بازگشت باید بین صفر و ${formatNumber(available)} کیلوگرم باشد.`, "error");
        return;
      }

      setSubmitting(true);
      try {
        const dateISO = nowISO();
        const updatedRoll: Roll = {
          ...roll,
          currentWeight: roundWeight(stockWeight(roll) + amount),
          totalReturned: roundWeight((roll.totalReturned || 0) + amount),
          status: "in",
          returnISO: dateISO,
          returnSource: values.returnSource.trim(),
          returnDoc: values.returnDoc.trim(),
          returnNote: values.returnNote.trim(),
        };

        const transaction: Transaction = {
          type: "return",
          rollId: roll.id,
          dateISO,
          weight: amount,
          source: values.returnSource.trim(),
          doc: values.returnDoc.trim(),
          note: values.returnNote.trim(),
        };

        const updatedDb = {
          ...db,
          rolls: db.rolls.map((r: Roll) => (r.id === roll.id ? updatedRoll : r)),
          transactions: [...db.transactions, transaction],
        };

        saveDB(updatedDb);
        setDb(updatedDb);
        setReload(!reload);
        returnForm.resetForm();
        setSelectedRoll(null);
        Swal.fire("عملیات موفق", `بازگشت ${formatNumber(amount)} کیلوگرم از بچ ${roll.batch} ثبت شد.`, "success");
      } catch (error) {
        console.error("Return error:", error);
        Swal.fire("خطا", apiErrorMessage(error, "مشکل در ثبت بازگشت"), "error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const returnableRolls = useMemo(() => {
    return db.rolls
      .filter((r: Roll) => outsideWeight(r) > 0)
      .sort((a, b) => String(a.batch).localeCompare(String(b.batch), "fa"));
  }, [db, reload]);

  const rollOptions = returnableRolls.map((r: Roll) => ({
    value: String(r.id),
    label: `بچ ${r.batch} | شناسه ${r.id} | قابل بازگشت ${formatNumber(outsideWeight(r))} کیلو | موجودی فعلی ${formatNumber(stockWeight(r))} کیلو`,
  }));

  const handleRollChange = (opt: any) => {
    returnForm.setFieldValue("rollId", opt?.value || "");
    const roll = db.rolls.find((r: Roll) => String(r.id) === opt?.value);
    setSelectedRoll(roll || null);
    if (roll) {
      returnForm.setFieldValue("weight", String(outsideWeight(roll)));
    } else {
      returnForm.setFieldValue("weight", "");
    }
  };

  const recentReturns = useMemo(() => {
    return [...db.transactions]
      .filter((t: Transaction) => t.type === "return")
      .sort((a, b) => new Date(b.dateISO).getTime() - new Date(a.dateISO).getTime())
      .slice(0, 8)
      .map((t) => ({
        ...t,
        roll: db.rolls.find((r: Roll) => r.id === t.rollId),
      }))
      .filter((t) => t.roll);
  }, [db, reload]);

  const returnColumns = useMemo(
    () => [
      {
        header: "شماره بچ",
        accessorKey: "roll.batch",
      },
      {
        header: "شناسه",
        accessorKey: "roll.id",
        cell: (cell: any) => <span className="font-medium">{cell.getValue()}</span>,
      },
      {
        header: "مشخصات رول",
        cell: (cell: any) => {
          const roll = cell.row.original.roll;
          return (
            <div className="text-sm">
              <div>عرض {roll.width} | ضخامت {roll.thickness}</div>
              <div className="text-xs text-slate-500 dark:text-zink-400">{roll.color}</div>
            </div>
          );
        },
      },
      {
        header: "وزن بازگشتی",
        accessorKey: "weight",
        cell: (cell: any) => <span>{formatNumber(cell.getValue())} کیلو</span>,
      },
      {
        header: "بازگرداننده",
        accessorKey: "source",
        cell: (cell: any) => cell.getValue() || "—",
      },
      {
        header: "تاریخ بازگشت",
        accessorKey: "dateISO",
        cell: (cell: any) => <span className="text-xs">{jalaliDateTime(cell.getValue())}</span>,
      },
    ],
    []
  );

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <div className="card border-0 shadow-md rounded-2xl">
        <div className="card-body p-5">
          <h5 className="text-base font-semibold text-slate-800 dark:text-white mb-4">فرم بازگشت کالا</h5>
          <div className="bg-blue-50 dark:bg-blue-500/10 rounded-lg p-3 mb-4 text-sm text-blue-700 dark:text-blue-300">
            حداکثر وزن قابل بازگشت برابر مقدار خالص خارج از انبار برای همان رول است.
          </div>

          <form onSubmit={returnForm.handleSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  انتخاب رول <span className="text-red-500">*</span>
                </label>
                <Select
                  options={rollOptions}
                  value={rollOptions.find((o) => o.value === returnForm.values.rollId) || null}
                  onChange={handleRollChange}
                  placeholder="انتخاب رول..."
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {returnForm.touched.rollId && returnForm.errors.rollId && (
                  <p className="text-red-400 text-xs mt-1">{returnForm.errors.rollId}</p>
                )}
              </div>

              {selectedRoll && (
                <div className="bg-emerald-50 dark:bg-emerald-500/10 rounded-lg p-3 text-sm text-emerald-700 dark:text-emerald-300">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="size-4 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-medium">شماره بچ {selectedRoll.batch}</span>، شناسه {selectedRoll.id}
                      <br />
                      حداکثر قابل بازگشت: <strong>{formatNumber(outsideWeight(selectedRoll))}</strong> کیلوگرم
                      <br />
                      موجودی فعلی: {formatNumber(stockWeight(selectedRoll))} کیلوگرم
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  وزن بازگشتی (کیلوگرم) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="form-input w-full"
                  placeholder="مقدار بازگشتی"
                  {...returnForm.getFieldProps("weight")}
                />
                {returnForm.touched.weight && returnForm.errors.weight && (
                  <p className="text-red-400 text-xs mt-1">{returnForm.errors.weight}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  بازگرداننده / مبدأ <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="form-input w-full"
                  placeholder="نام شخص، واحد یا مشتری"
                  maxLength={100}
                  {...returnForm.getFieldProps("returnSource")}
                />
                {returnForm.touched.returnSource && returnForm.errors.returnSource && (
                  <p className="text-red-400 text-xs mt-1">{returnForm.errors.returnSource}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  شماره سند بازگشت (اختیاری)
                </label>
                <input
                  type="text"
                  className="form-input w-full"
                  placeholder="مثال: R-1024"
                  maxLength={80}
                  {...returnForm.getFieldProps("returnDoc")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-zink-200">
                  توضیحات بازگشت (اختیاری)
                </label>
                <textarea
                  className="form-input w-full resize-y min-h-[60px]"
                  placeholder="توضیحات تکمیلی"
                  maxLength={500}
                  {...returnForm.getFieldProps("returnNote")}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-white border-t-transparent" />
                    در حال ثبت...
                  </>
                ) : (
                  <>
                    <Undo2 className="size-4" />
                    ثبت بازگشت به انبار
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  returnForm.resetForm();
                  setSelectedRoll(null);
                }}
                className="px-6 py-2.5 bg-slate-200 dark:bg-zink-600 hover:bg-slate-300 dark:hover:bg-zink-500 rounded-lg transition-colors"
              >
                پاک کردن فرم
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card border-0 shadow-md rounded-2xl">
        <div className="card-body p-5">
          <h5 className="text-base font-semibold text-slate-800 dark:text-white mb-4">آخرین بازگشت‌ها</h5>
          {recentReturns.length > 0 ? (
            <TableContainer
              isPagination={false}
              columns={returnColumns}
              data={recentReturns}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600 text-xs"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80 text-sm"
            />
          ) : (
            <div className="text-center py-8 text-slate-400 dark:text-zink-400">
              <p>بازگشتی ثبت نشده است</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ========== Inventory Content ==========
const InventoryContent: React.FC = () => {
  const [db, setDb] = useState<DB>(() => loadDB());
  const [reload, setReload] = useState(false);
  const [filteredRolls, setFilteredRolls] = useState<Roll[]>([]);
  const [filters, setFilters] = useState({
    search: "",
    status: "all" as "all" | "in" | "out" | "partial",
    width: "",
    thickness: "",
    color: "",
    supplier: "",
    minWeight: "",
    maxWeight: "",
  });

  const applyFilters = useCallback(() => {
    let rolls = db.rolls;

    // جستجو
    if (filters.search) {
      const q = filters.search.toLowerCase();
      rolls = rolls.filter(
        (r) =>
          String(r.id).includes(q) ||
          r.batch.toLowerCase().includes(q) ||
          r.supplier.toLowerCase().includes(q)
      );
    }

    // وضعیت
    if (filters.status === "in") {
      rolls = rolls.filter((r) => stockWeight(r) > 0);
    } else if (filters.status === "out") {
      rolls = rolls.filter((r) => stockWeight(r) <= 0);
    } else if (filters.status === "partial") {
      rolls = rolls.filter((r) => stockWeight(r) > 0 && outsideWeight(r) > 0);
    }

    // عرض
    if (filters.width) {
      rolls = rolls.filter((r) => r.width === filters.width);
    }

    // ضخامت
    if (filters.thickness) {
      rolls = rolls.filter((r) => r.thickness === filters.thickness);
    }

    // رنگ
    if (filters.color) {
      rolls = rolls.filter((r) => r.color === filters.color);
    }

    // تأمین‌کننده
    if (filters.supplier) {
      rolls = rolls.filter((r) => r.supplier === filters.supplier);
    }

    // محدوده وزنی
    const minW = parseFloat(filters.minWeight);
    const maxW = parseFloat(filters.maxWeight);
    if (!isNaN(minW)) {
      rolls = rolls.filter((r) => stockWeight(r) >= minW);
    }
    if (!isNaN(maxW)) {
      rolls = rolls.filter((r) => stockWeight(r) <= maxW);
    }

    rolls.sort((a, b) => b.id - a.id);
    setFilteredRolls(rolls);
  }, [db, filters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters, reload]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "all",
      width: "",
      thickness: "",
      color: "",
      supplier: "",
      minWeight: "",
      maxWeight: "",
    });
  };

  const widthOptions = WIDTHS.map((w) => ({ value: w, label: w }));
  const thicknessOptions = THICKNESSES.map((t) => ({ value: t, label: t }));
  const colorOptions = COLORS.map((c) => ({ value: c, label: c }));
  const supplierOptions = db.suppliers.map((s: string) => ({ value: s, label: s }));
  const statusOptions = [
    { value: "all", label: "همه" },
    { value: "in", label: "موجود" },
    { value: "out", label: "خارج‌شده" },
    { value: "partial", label: "موجود جزئی" },
  ];

  const inventoryColumns = useMemo(
    () => [
      {
        header: "شناسه",
        accessorKey: "id",
        cell: (cell: any) => <span className="font-medium">{cell.getValue()}</span>,
      },
      {
        header: "وضعیت",
        cell: (cell: any) => {
          const r: Roll = cell.row.original;
          const stock = stockWeight(r);
          const outside = outsideWeight(r);
          let label = "موجود";
          let className = "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400";
          if (stock <= 0) {
            label = "خارج‌شده";
            className = "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400";
          } else if (outside > 0) {
            label = "موجود جزئی";
            className = "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400";
          }
          return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>{label}</span>;
        },
      },
      {
        header: "تاریخ ورود",
        accessorKey: "entryISO",
        cell: (cell: any) => <span className="text-xs">{jalaliDateTime(cell.getValue())}</span>,
      },
      {
        header: "عرض",
        accessorKey: "width",
      },
      {
        header: "ضخامت",
        accessorKey: "thickness",
      },
      {
        header: "رنگ",
        cell: (cell: any) => {
          const color = cell.row.original.color;
          return (
            <span className="flex items-center gap-1.5">
              <span className="inline-block size-3 rounded-full" style={{ background: colorHex(color) }} />
              {color}
            </span>
          );
        },
      },
      {
        header: "وزن اولیه",
        accessorKey: "originalWeight",
        cell: (cell: any) => formatNumber(cell.getValue()),
      },
      {
        header: "موجودی فعلی",
        cell: (cell: any) => {
          const r: Roll = cell.row.original;
          return <strong>{formatNumber(stockWeight(r))}</strong>;
        },
      },
      {
        header: "خارج از انبار",
        cell: (cell: any) => {
          const r: Roll = cell.row.original;
          return formatNumber(outsideWeight(r));
        },
      },
      {
        header: "تأمین‌کننده",
        accessorKey: "supplier",
      },
      {
        header: "شماره بچ",
        accessorKey: "batch",
      },
    ],
    []
  );

  const totalWeight = filteredRolls.reduce((s, r) => s + stockWeight(r), 0);
  const totalOutside = filteredRolls.reduce((s, r) => s + outsideWeight(r), 0);
  const inCount = filteredRolls.filter((r) => stockWeight(r) > 0).length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <div className="card border-0 shadow-md rounded-2xl">
          <div className="card-body p-4">
            <p className="text-sm text-slate-500 dark:text-zink-400">جمع موجودی فعلی</p>
            <h4 className="text-xl font-bold text-slate-800 dark:text-white">{formatNumber(totalWeight)} کیلو</h4>
          </div>
        </div>
        <div className="card border-0 shadow-md rounded-2xl">
          <div className="card-body p-4">
            <p className="text-sm text-slate-500 dark:text-zink-400">جمع وزن خارج از انبار</p>
            <h4 className="text-xl font-bold text-slate-800 dark:text-white">{formatNumber(totalOutside)} کیلو</h4>
          </div>
        </div>
        <div className="card border-0 shadow-md rounded-2xl">
          <div className="card-body p-4">
            <p className="text-sm text-slate-500 dark:text-zink-400">تعداد رول دارای موجودی</p>
            <h4 className="text-xl font-bold text-slate-800 dark:text-white">{inCount}</h4>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-md rounded-2xl">
        <div className="card-body p-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="block text-xs font-medium mb-1 text-slate-500 dark:text-zink-400">جستجو</label>
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
                <input
                  type="text"
                  className="form-input w-full pr-9 text-sm"
                  placeholder="شناسه، بچ یا تأمین‌کننده"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1 text-slate-500 dark:text-zink-400">وضعیت</label>
              <Select
                options={statusOptions}
                value={statusOptions.find((o) => o.value === filters.status) || null}
                onChange={(opt: any) => handleFilterChange("status", opt?.value || "all")}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1 text-slate-500 dark:text-zink-400">عرض</label>
              <Select
                options={[{ value: "", label: "همه" }, ...widthOptions]}
                value={[{ value: "", label: "همه" }, ...widthOptions].find((o) => o.value === filters.width) || null}
                onChange={(opt: any) => handleFilterChange("width", opt?.value || "")}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1 text-slate-500 dark:text-zink-400">ضخامت</label>
              <Select
                options={[{ value: "", label: "همه" }, ...thicknessOptions]}
                value={[{ value: "", label: "همه" }, ...thicknessOptions].find((o) => o.value === filters.thickness) || null}
                onChange={(opt: any) => handleFilterChange("thickness", opt?.value || "")}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1 text-slate-500 dark:text-zink-400">رنگ</label>
              <Select
                options={[{ value: "", label: "همه" }, ...colorOptions]}
                value={[{ value: "", label: "همه" }, ...colorOptions].find((o) => o.value === filters.color) || null}
                onChange={(opt: any) => handleFilterChange("color", opt?.value || "")}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                formatOptionLabel={(opt: any) => (
                  <span className="flex items-center gap-2">
                    {opt.value && <span className="inline-block size-3 rounded-full" style={{ background: colorHex(opt.value) }} />}
                    {opt.label}
                  </span>
                )}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1 text-slate-500 dark:text-zink-400">تأمین‌کننده</label>
              <Select
                options={[{ value: "", label: "همه" }, ...supplierOptions]}
                value={[{ value: "", label: "همه" }, ...supplierOptions].find((o) => o.value === filters.supplier) || null}
                onChange={(opt: any) => handleFilterChange("supplier", opt?.value || "")}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1 text-slate-500 dark:text-zink-400">حداقل موجودی</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-input w-full text-sm"
                placeholder="۰"
                value={filters.minWeight}
                onChange={(e) => handleFilterChange("minWeight", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1 text-slate-500 dark:text-zink-400">حداکثر موجودی</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-input w-full text-sm"
                placeholder="∞"
                value={filters.maxWeight}
                onChange={(e) => handleFilterChange("maxWeight", e.target.value)}
              />
            </div>

            <div className="flex items-end gap-2 md:col-span-2 lg:col-span-4">
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-slate-200 dark:bg-zink-600 hover:bg-slate-300 dark:hover:bg-zink-500 rounded-lg transition-colors"
              >
                <X className="size-4" />
                بازنشانی فیلترها
              </button>
              <button
                onClick={applyFilters}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm text-white bg-custom-500 hover:bg-custom-600 rounded-lg transition-colors"
              >
                <Filter className="size-4" />
                اعمال فیلتر
              </button>
            </div>
          </div>

          <div className="mt-5">
            {filteredRolls.length > 0 ? (
              <TableContainer
                isPagination={true}
                columns={inventoryColumns}
                data={filteredRolls}
                customPageSize={10}
                customnumberfortable={10}
                divclassName="-mx-5 overflow-x-auto"
                tableclassName="w-full whitespace-nowrap"
                theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600 text-xs"
                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80 text-sm"
                PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
              />
            ) : (
              <div className="text-center py-8 text-slate-400 dark:text-zink-400">
                <p>نتیجه‌ای با فیلترهای انتخاب‌شده پیدا نشد.</p>
              </div>
            )}
          </div>

          <div className="mt-3 text-sm text-slate-500 dark:text-zink-400">
            تعداد نتایج: <strong>{filteredRolls.length}</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== Suppliers Content ==========
const SuppliersContent: React.FC = () => {
  const [db, setDb] = useState<DB>(() => loadDB());
  const [reload, setReload] = useState(false);
  const [newSupplier, setNewSupplier] = useState("");

  const handleAddSupplier = () => {
    const name = newSupplier.trim();
    if (!name) {
      Swal.fire("خطا", "لطفاً نام تأمین‌کننده را وارد کنید", "error");
      return;
    }
    if (db.suppliers.some((s) => s.toLowerCase() === name.toLowerCase())) {
      Swal.fire("خطا", "این تأمین‌کننده قبلاً ثبت شده است", "error");
      return;
    }
    const updatedDb = {
      ...db,
      suppliers: [...db.suppliers, name].sort((a, b) => a.localeCompare(b, "fa")),
    };
    saveDB(updatedDb);
    setDb(updatedDb);
    setReload(!reload);
    setNewSupplier("");
    Swal.fire("عملیات موفق", "تأمین‌کننده با موفقیت افزوده شد", "success");
  };

  const handleDeleteSupplier = (name: string) => {
    if (db.rolls.some((r) => r.supplier === name)) {
      Swal.fire("خطا", "این تأمین‌کننده در سوابق استفاده شده است و قابل حذف نیست", "error");
      return;
    }
    Swal.fire({
      title: "حذف تأمین‌کننده",
      text: `آیا از حذف «${name}» اطمینان دارید؟`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "بله، حذف کن",
      cancelButtonText: "لغو",
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedDb = {
          ...db,
          suppliers: db.suppliers.filter((s) => s !== name),
        };
        saveDB(updatedDb);
        setDb(updatedDb);
        setReload(!reload);
        Swal.fire("عملیات موفق", "تأمین‌کننده حذف شد", "success");
      }
    });
  };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <div className="card border-0 shadow-md rounded-2xl">
        <div className="card-body p-5">
          <h5 className="text-base font-semibold text-slate-800 dark:text-white mb-4">افزودن تأمین‌کننده</h5>
          <div className="flex gap-3">
            <input
              type="text"
              className="form-input flex-1"
              placeholder="نام شرکت یا فروشنده"
              value={newSupplier}
              onChange={(e) => setNewSupplier(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddSupplier()}
              maxLength={100}
            />
            <button
              onClick={handleAddSupplier}
              className="inline-flex items-center gap-1 px-4 py-2 text-white bg-custom-500 hover:bg-custom-600 rounded-lg transition-colors"
            >
              <Plus className="size-4" />
              افزودن
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-400 dark:text-zink-400">
            تأمین‌کننده‌ای که در اطلاعات یک رول استفاده شده است، برای حفظ سابقه قابل حذف نیست.
          </p>
        </div>
      </div>

      <div className="card border-0 shadow-md rounded-2xl">
        <div className="card-body p-5">
          <h5 className="text-base font-semibold text-slate-800 dark:text-white mb-4">فهرست تأمین‌کنندگان</h5>
          {db.suppliers.length > 0 ? (
            <div className="space-y-2">
              {db.suppliers.map((s) => {
                const used = db.rolls.some((r) => r.supplier === s);
                return (
                  <div
                    key={s}
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zink-700/30 rounded-lg border border-slate-200 dark:border-zink-600"
                  >
                    <span className="text-sm font-medium text-slate-700 dark:text-zink-200">{s}</span>
                    <button
                      onClick={() => handleDeleteSupplier(s)}
                      disabled={used}
                      className={`inline-flex items-center gap-1 px-3 py-1 text-xs rounded-lg transition-colors ${used
                        ? "bg-slate-200 text-slate-400 dark:bg-zink-600 dark:text-zink-400 cursor-not-allowed"
                        : "bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-500/20 dark:text-red-400 dark:hover:bg-red-500/30"
                        }`}
                      title={used ? "در حال استفاده" : "حذف"}
                    >
                      <Trash2 className="size-3" />
                      {used ? "در حال استفاده" : "حذف"}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 dark:text-zink-400">
              <p>هیچ تأمین‌کننده‌ای ثبت نشده است</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ========== Backup Content ==========
const BackupContent: React.FC = () => {
  const handleExport = () => {
    try {
      const db = loadDB();
      const data = {
        ...db,
        exportedAt: nowISO(),
        exportedAtJalali: jalaliDateTime(nowISO()),
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sheet-roll-backup-${getPersianDate().replaceAll("/", "-")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      Swal.fire("عملیات موفق", "فایل پشتیبان با موفقیت دانلود شد", "success");
    } catch (error) {
      console.error("Export error:", error);
      Swal.fire("خطا", apiErrorMessage(error, "مشکل در ایجاد فایل پشتیبان"), "error");
    }
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        if (!data.rolls || !data.suppliers || !data.transactions) {
          throw new Error("فرمت فایل نامعتبر است");
        }
        Swal.fire({
          title: "بازیابی اطلاعات",
          text: "اطلاعات فعلی با فایل پشتیبان جایگزین خواهد شد. ادامه می‌دهید؟",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "بله",
          cancelButtonText: "لغو",
        }).then((result) => {
          if (result.isConfirmed) {
            saveDB(data);
            Swal.fire("عملیات موفق", "اطلاعات با موفقیت بازیابی شد", "success");
            window.location.reload();
          }
        });
      } catch (error) {
        console.error("Import error:", error);
        Swal.fire("خطا", apiErrorMessage(error, "فایل پشتیبان معتبر نیست"), "error");
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const handleReset = () => {
    Swal.fire({
      title: "حذف همه اطلاعات",
      text: "همه رول‌ها، تراکنش‌ها و تأمین‌کنندگان حذف خواهند شد. این عملیات قابل بازگشت نیست!",
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "بله، حذف کن",
      cancelButtonText: "لغو",
    }).then((result) => {
      if (result.isConfirmed) {
        saveDB(defaultDB());
        Swal.fire("عملیات موفق", "همه اطلاعات با موفقیت حذف شد", "success");
        window.location.reload();
      }
    });
  };

  return (
    <div className="card border-0 shadow-md rounded-2xl">
      <div className="card-body p-5">
        <div className="bg-yellow-50 dark:bg-yellow-500/10 rounded-lg p-4 mb-6 text-sm text-yellow-700 dark:text-yellow-300">
          <AlertCircle className="inline size-4 mr-2" />
          قبل از پاک کردن حافظه مرورگر یا انتقال سیستم به رایانه دیگر، از اطلاعات فایل پشتیبان تهیه کنید.
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <button
            onClick={handleExport}
            className="flex items-center justify-center gap-2 px-6 py-3 text-white bg-custom-500 hover:bg-custom-600 rounded-lg transition-colors"
          >
            <Download className="size-5" />
            دانلود فایل پشتیبان JSON
          </button>

          <label className="flex items-center justify-center gap-2 px-6 py-3 text-slate-700 dark:text-zink-200 bg-slate-200 dark:bg-zink-600 hover:bg-slate-300 dark:hover:bg-zink-500 rounded-lg transition-colors cursor-pointer">
            <Upload className="size-5" />
            بازیابی از فایل
            <input type="file" accept=".json" className="hidden" onChange={handleImport} />
          </label>

          <button
            onClick={handleReset}
            className="flex items-center justify-center gap-2 px-6 py-3 text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
          >
            <Trash2 className="size-5" />
            حذف همه اطلاعات
          </button>
        </div>

        <p className="mt-4 text-xs text-slate-400 dark:text-zink-400">
          ذخیره‌سازی با LocalStorage انجام می‌شود؛ بنابراین این فایل برای استفاده محلی و سیستم‌های ساده مناسب است.
          برای کار چندکاربره باید به پایگاه داده و سرور متصل شود.
        </p>
      </div>
    </div>
  );
};

// ========== Tabs Definition ==========
const NAV_TABS = [
  { key: "dashboard", label: "داشبورد", icon: <LayoutDashboard size={18} /> },
  { key: "entry", label: "ورود به انبار", icon: <ArrowRightLeft size={18} /> },
  { key: "exit", label: "خروج از انبار", icon: <ArrowLeftRight size={18} /> },
  { key: "return", label: "بازگشت به انبار", icon: <Undo2 size={18} /> },
  { key: "inventory", label: "موجودی و گزارش", icon: <PackageSearch size={18} /> },
  { key: "suppliers", label: "تأمین‌کنندگان", icon: <Truck size={18} /> },
  { key: "backup", label: "پشتیبان‌گیری", icon: <Database size={18} /> },
];

// ========== Main Component ==========
const Warehouse: React.FC = () => {
  return (
    <React.Fragment>
      <BreadCrumb pageTitle="انبار" title="مدیریت انبار" />

      <Tab.Container defaultActiveKey="dashboard">
        <div className="card border-0 shadow-md rounded-2xl overflow-hidden">
          <div className="!py-0 card-body bg-white dark:bg-zink-800">
            <Nav className="flex flex-wrap items-center gap-1 w-full overflow-x-auto text-sm font-medium border-b border-gray-200 dark:border-zink-600">
              {NAV_TABS.map((tab) => (
                <Nav.Item key={tab.key} eventKey={tab.key} className="group relative">
                  <a
                    href="#!"
                    className={`
                      flex items-center gap-2 px-4 py-3 text-sm transition-all duration-200
                      text-gray-600 dark:text-zink-200
                      border-b-2 border-transparent
                      group-[.active]:text-[#C0151B] dark:group-[.active]:text-[#C0151B]
                      group-[.active]:border-[#C0151B]
                      hover:text-[#C0151B] dark:hover:text-[#C0151B]
                      focus:outline-none
                      whitespace-nowrap
                    `}
                  >
                    <span className="text-inherit">{tab.icon}</span>
                    {tab.label}
                  </a>
                </Nav.Item>
              ))}
            </Nav>
          </div>
        </div>

        <Tab.Content className="mt-5">
          <Tab.Pane eventKey="dashboard">
            <div className="card border-0 shadow-md rounded-2xl bg-white dark:bg-zink-800">
              <div className="card-body">
                <DashboardContent />
              </div>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="entry">
            <div className="card border-0 shadow-md rounded-2xl bg-white dark:bg-zink-800">
              <div className="card-body">
                <EntryContent />
              </div>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="exit">
            <div className="card border-0 shadow-md rounded-2xl bg-white dark:bg-zink-800">
              <div className="card-body">
                <ExitContent />
              </div>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="return">
            <div className="card border-0 shadow-md rounded-2xl bg-white dark:bg-zink-800">
              <div className="card-body">
                <ReturnContent />
              </div>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="inventory">
            <div className="card border-0 shadow-md rounded-2xl bg-white dark:bg-zink-800">
              <div className="card-body">
                <InventoryContent />
              </div>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="suppliers">
            <div className="card border-0 shadow-md rounded-2xl bg-white dark:bg-zink-800">
              <div className="card-body">
                <SuppliersContent />
              </div>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="backup">
            <div className="card border-0 shadow-md rounded-2xl bg-white dark:bg-zink-800">
              <div className="card-body">
                <BackupContent />
              </div>
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </React.Fragment>
  );
};

export default Warehouse;